import "mapbox-gl/dist/mapbox-gl.css";
import "mapbox-gl-compare/dist/mapbox-gl-compare.css";

import React, { useEffect, useRef, useState } from "react";
import mapboxgl, { LngLat } from "mapbox-gl";
import MapboxCompare from "mapbox-gl-compare";
import mapboxglCompare from "mapbox-gl-compare";
import { Typography, Box } from "@mui/material";

import { mapDataCloudfrontURL } from "../../../map/MapConstants";
import { ScaleBar } from "../../../map/ScaleBar";
import { calcLngLatByEventCentroid } from "../../emergencyUtils";
import { useClientConfig } from "../../../../context/clientConfigContext";

const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const mapCenter: LngLat = new LngLat(0, 0);
const expImagesUrl = `${mapDataCloudfrontURL}terrasheld/exp_images/`;
const initialZoom = 8;

const mapCssStyle: React.CSSProperties = {
  position: "absolute",
  width: "100%",
  height: "100%",
};

type CoordinatesArray = [[number, number], [number, number], [number, number], [number, number]];

type ImageData = {
  imagePath: string;
  coordinates: CoordinatesArray;
  imageDate: string;
};

interface MapCompareProps {
  event: TerraShieldEvent;
  aoiList: TerraShieldEventAoi[];
}

export const MapSlider: React.FC<MapCompareProps> = ({ event, aoiList }) => {
  const mapRef = useRef<MapboxCompare>();
  const beforeMapRef = useRef<mapboxgl.Map | null>(null);
  const afterMapRef = useRef<mapboxgl.Map | null>(null);
  const beforeMapContainerRef = useRef<any>();
  const afterMapContainerRef = useRef<any>();
  const comparisonContainerRef = useRef<any>();

  const [zoom, setZoom] = useState<number>(8);
  const [latitude, setLatitude] = useState(mapCenter.lat);
  const [longitude, setLongitude] = useState(mapCenter.lng);

  const [mapImagesList, setMapImagesList] = useState<ImageData[]>([]);
  const [aoiFilePaths, setAoiFilePaths] = useState<string[]>([]);
  const subdirectory = useClientConfig().emergencyGeoJsonSubdirectory;

  useEffect(() => {
    if (mapRef.current) return;

    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
    if (!beforeMapContainerRef.current || !afterMapContainerRef.current || !comparisonContainerRef.current) {
      console.error("One or more container refs are missing.");
      return;
    }

    beforeMapRef.current = new mapboxgl.Map({
      container: beforeMapContainerRef.current,
      style: "mapbox://styles/mapbox/satellite-v9",
      center: [mapCenter.lng, mapCenter.lat],
      zoom: initialZoom,
    });

    // Set up zoom change event listeners
    beforeMapRef.current.on("zoom", () => {
      if (beforeMapRef.current) {
        const currentZoom = beforeMapRef.current.getZoom();
        setZoom(currentZoom);
      }
    });

    afterMapRef.current = new mapboxgl.Map({
      container: afterMapContainerRef.current,
      style: "mapbox://styles/mapbox/light-v10",
      center: [mapCenter.lng, mapCenter.lat],
      zoom: initialZoom,
    });

    mapRef.current = new mapboxglCompare(
      beforeMapRef.current,
      afterMapRef.current,
      comparisonContainerRef.current,
    );
    // Cleanup function
    return () => {
      if (beforeMapRef.current) beforeMapRef.current.remove();
      if (afterMapRef.current) afterMapRef.current.remove();
      if (mapRef.current) mapRef.current = undefined;
    };
  }, []);

  useEffect(() => {
    if (!beforeMapRef.current || !afterMapRef.current) return;

    beforeMapRef.current.setCenter([longitude, latitude]);
    afterMapRef.current.setCenter([longitude, latitude]);
  }, [latitude, longitude]);

  useEffect(() => {
    const fetchImgData = async () => {
      try {
        const timeStampToAvoidCachedStorage = new Date().getTime();
        const configurationFileName = "extra_images_catalog.json";
        const fetchResponse = await fetch(
          `${expImagesUrl}${configurationFileName}?t=${timeStampToAvoidCachedStorage}`,
        );

        const imgCatalogResponse = await fetchResponse.json();
        const currEventImgData: [{ image: string; corners: string; imageDate: string }] =
          imgCatalogResponse[event.code];

        if (!currEventImgData?.length) return;
        const localImgList: ImageData[] = [];

        for (let imgData of currEventImgData) {
          const imgFileName = imgData.image;
          const imgCornersFileName = imgData.corners;

          const cornersFetchResponse = await fetch(`${expImagesUrl}${imgCornersFileName}`);
          const imgCornersResponse: CoordinatesArray = await cornersFetchResponse.json();

          localImgList.push({
            imagePath: imgFileName,
            coordinates: imgCornersResponse,
            imageDate: imgData.imageDate,
          });
        }

        setMapImagesList([localImgList[0]]); // display one img for now
      } catch (err) {
        console.error("Error fetching map images", err);
      }
    };

    if (Object.keys(event).length !== 0 && beforeMapRef.current && afterMapRef.current) {
      const { lngLat } = calcLngLatByEventCentroid(event.extent);
      setLatitude(lngLat.lat);
      setLongitude(lngLat.lng);

      beforeMapRef.current.setCenter(lngLat);
      afterMapRef.current.setCenter(lngLat);

      fetchImgData();
    }
  }, [event]);

  useEffect(() => {
    if (aoiList.length > 0) {
      const localAoiPaths: string[] = [];

      for (const aoi of aoiList) {
        const s3AoiFolder = `AOI${aoi.number.toString().padStart(2, "0")}`;

        const aoiFilePath = `${subdirectory}/${aoi.activationCode}/${s3AoiFolder}/aois.geojson`;
        localAoiPaths.push(mapDataCloudfrontURL + aoiFilePath);
      }
      setAoiFilePaths(localAoiPaths);

      // if (localAoiPaths.length > 0) {
      //   aoiFilePaths.map((aoiFilePath, index) => {
      //     afterMap.addSource(`aoiLine-${index}`, {
      //       type: "geojson",
      //       data: aoiFilePath,
      //     });

      //     afterMap.addLayer({
      //       id: `aoiLayerLine-${index}`,
      //       type: "line",
      //       source: `aoiLine-${index}`,
      //       paint: {
      //         "line-color": "#ff0000",
      //         "line-width": 1,
      //       },
      //     });
      //   });
      // }
    }
  }, [aoiList]);

  // Add AOI layers when paths change
  useEffect(() => {
    if (!afterMapRef.current || !beforeMapRef.current || aoiFilePaths.length === 0) return;

    const addAoiLayers = (mapRefCurrent: mapboxgl.Map | null) => {
      aoiFilePaths.forEach((aoiFilePath, index) => {
        if (!mapRefCurrent) return;

        mapRefCurrent.addSource(`aoiLine-${index}`, {
          type: "geojson",
          data: aoiFilePath,
        });

        mapRefCurrent.addLayer({
          id: `aoiLayerLine-${index}`,
          type: "line",
          source: `aoiLine-${index}`,
          paint: {
            "line-color": "#ff0000",
            "line-width": 1,
          },
        });
      });
    };

    // Clean up existing layers before adding new ones - not sure if needed
    // aoiFilePaths.forEach((_, index) => {
    //   const layerId = `aoiLayerLine-${index}`;
    //   const sourceId = `aoiLine-${index}`;

    //   if (afterMapRef.current?.getLayer(layerId)) {
    //     afterMapRef.current.removeLayer(layerId);
    //   }

    //   if (afterMapRef.current?.getSource(sourceId)) {
    //     afterMapRef.current.removeSource(sourceId);
    //   }
    // });

    // Make sure the map is loaded before adding sources and layers
    if (!afterMapRef.current.loaded()) {
      afterMapRef.current.on("load", () => {
        addAoiLayers(afterMapRef.current);
      });
    } else {
      addAoiLayers(afterMapRef.current);
    }

    // Make sure the map is loaded before adding sources and layers
    if (!beforeMapRef.current.loaded()) {
      beforeMapRef.current.on("load", () => {
        addAoiLayers(beforeMapRef.current);
      });
    } else {
      addAoiLayers(beforeMapRef.current);
    }
  }, [aoiFilePaths]);

  // // Add images layers when they are retrieved
  useEffect(() => {
    if (!afterMapRef.current || mapImagesList.length === 0) return;

    const addImagesLayers = (mapRefCurrent: mapboxgl.Map | null) => {
      mapImagesList.forEach((imgData, index) => {
        if (!mapRefCurrent) return;

        mapRefCurrent.addSource(`extra-map-image${index}`, {
          type: "image",
          url: `${expImagesUrl}${imgData.imagePath}`,
          coordinates: imgData.coordinates,
        });

        mapRefCurrent.addLayer({
          id: `extra-map-image-layer${index}`,
          type: "raster",
          source: `extra-map-image${index}`,
          paint: {
            // "raster-opacity": 0.9,
          },
        });
      });
    };

    if (!afterMapRef.current.loaded()) {
      afterMapRef.current.on("load", () => {
        addImagesLayers(afterMapRef.current);
      });
    } else {
      addImagesLayers(afterMapRef.current);
    }
  }, [mapImagesList]);

  return (
    <div
      id="comparison-container"
      ref={comparisonContainerRef}
      style={{ height: "100%", position: "relative" }}
    >
      <div id="before" ref={beforeMapContainerRef} style={mapCssStyle}></div>
      <div id="after" ref={afterMapContainerRef} style={mapCssStyle}></div>

      {mapImagesList.length && mapImagesList[0].imageDate && (
        <Box
          sx={{
            backgroundColor: "white",
            right: "35px",
            marginTop: "10px",
            padding: "5px 10px",
            position: "absolute",
          }}
        >
          <Typography variant="body2">After event date: {mapImagesList[0].imageDate}</Typography>
        </Box>
      )}
      <ScaleBar zoom={zoom} />
    </div>
  );
};
